<template>
    <div class="ei-data" :style="eiDeltaColorCssVars">
        <div class="ei-data-prod-toolbar-wrap">
            <div class="ei-data-prod-toolbar">
                <div class="ei-data-toolbar-right">
                    <div
                        class="ei-data-edit-toggle"
                        :class="{ 'ei-data-edit-toggle--on': isEditMode, 'ei-data-edit-toggle--disabled': saving }"
                    >
                        <span class="ei-data-edit-toggle-label">편집 모드</span>
                        <button
                            type="button"
                            class="ei-data-edit-toggle-switch"
                            :disabled="saving"
                            role="switch"
                            :aria-checked="isEditMode ? 'true' : 'false'"
                            aria-label="편집 모드 전환"
                            @click="toggleEditMode"
                        >
                            <span class="ei-data-edit-toggle-track">
                                <span class="ei-data-edit-toggle-thumb"></span>
                            </span>
                        </button>
                    </div>
                </div>
                <div class="ei-data-toolbar-left">
                    <div class="ei-data-action-group">
                        <button
                            type="button"
                            class="ei-data-btn ei-data-btn--icon"
                            :disabled="saving || !isEditMode"
                            :title="isProductCategory ? '데이터 입력 행 추가' : '데이터 입력'"
                            :aria-label="isProductCategory ? '데이터 입력 행 추가' : '데이터 입력'"
                            @click="isProductCategory ? addNextMonthRow() : openFixedInputModal()"
                        >
                            <i :class="isProductCategory ? 'fas fa-plus' : 'fas fa-pen'"></i>
                        </button>
                    </div>
                    <div class="ei-data-action-group">
                        <button
                            type="button"
                            class="ei-data-btn ei-data-btn--icon ei-data-btn--cancel"
                            :disabled="!isEditMode || saving || dirtyCount === 0"
                            title="전체 변경 취소"
                            aria-label="전체 변경 취소"
                            @click="resetDraft"
                        >
                            <i class="fas fa-times"></i>
                        </button>
                    </div>
                    <div class="ei-data-action-group">
                        <button
                            type="button"
                            class="ei-data-btn ei-data-btn--save-pill"
                            :disabled="!isEditMode || saving || recalcLoading || dirtyCount === 0"
                            title="기준값 저장만 (재계산은 별도)"
                            aria-label="저장"
                            @click="saveRows"
                        >
                            {{ saving ? "저장 중..." : "저장" }}
                        </button>
                    </div>
                    <div class="ei-data-action-group">
                        <button
                            type="button"
                            class="ei-data-btn ei-data-btn--outline-pill"
                            :disabled="saving || recalcLoading"
                            title="현재 조회 구간 재계산"
                            aria-label="재계산"
                            @click="recalcCurrentPeriod"
                        >
                            {{ recalcLoading ? "재계산 중..." : "재계산" }}
                        </button>
                    </div>
                    <div v-if="showSummarySyncButton" class="ei-data-action-group">
                        <button
                            type="button"
                            class="ei-data-btn ei-data-btn--outline-pill"
                            :disabled="saving || recalcLoading || syncLoading"
                            title="기준 관제점 월 요약 → 기준값 입력 반영"
                            aria-label="요약에서 기준값 동기화"
                            @click="syncDenominatorFromSummary"
                        >
                            {{ syncLoading ? "동기화 중..." : "데이터 동기화" }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="ei-data-prod-card">
            <div class="ei-data-table-wrap">
                <table class="ei-data-table">
                    <colgroup>
                        <col />
                        <col />
                        <col />
                        <col />
                        <col />
                        <col />
                        <col />
                    </colgroup>
                    <thead>
                        <tr>
                            <th class="cell-center" :class="{ 'th-month-editable--active': isEditMode }">월</th>
                            <th class="cell-num">대상 관제점 값{{ energyUnitLabel }}</th>
                            <th class="cell-num th-base-editable" :class="{ 'th-base-editable--active': isEditMode }">
                                {{ baseColLabel }}
                            </th>
                            <th class="cell-num">{{ resultColLabel }}</th>
                            <th class="cell-num">전월 대비</th>
                            <th class="cell-num">전년 대비</th>
                            <th class="cell-center">상태</th>
                        </tr>
                    </thead>
                    <tbody v-if="isProductCategory">
                        <tr
                            v-for="row in draftRows"
                            :key="row._rowId"
                            :class="{
                                'row-dirty': rowDirty(row),
                                'row-new': row._isNew,
                                'row-focus-month': isQueryFocusMonthRow(row),
                            }"
                        >
                            <td
                                class="cell-center cell-editable cell-month-editable"
                                :class="{
                                    'cell-editable--locked': !isEditMode,
                                    'cell-month-editable--active': isEditMode,
                                }"
                            >
                                <DatePicker
                                    v-model="row._monthInput"
                                    type="month"
                                    format="YYYY-MM"
                                    value-type="format"
                                    :clearable="false"
                                    :disabled="!isEditMode || saving"
                                    class="ei-data-month-dp"
                                    :class="{ 'is-locked': !isEditMode || saving }"
                                    @change="onRowMonthChange(row)"
                                />
                            </td>
                            <td class="cell-num">{{ fmt(rowCalc(row)?.refKwhVal) }}</td>
                            <td
                                class="cell-num cell-editable cell-base-editable"
                                :class="{
                                    'cell-editable--locked': !isEditMode,
                                    'cell-base-editable--active': isEditMode,
                                }"
                            >
                                <input
                                    v-model.number="row.inputVal"
                                    class="ei-data-input ei-data-input--cell"
                                    type="number"
                                    step="any"
                                    :disabled="!isEditMode || saving"
                                    @input="markDirty(row)"
                                />
                            </td>
                            <td class="cell-num">{{ fmt(rowCalc(row)?.calcVal) }}</td>
                            <td class="cell-num cell-compare">
                                <div class="ei-data-compare-cell">
                                    <span class="ei-data-compare-main" :class="compareDeltaCls(row, 'mom')">
                                        {{ compareDeltaFmt(row, "mom") }}
                                    </span>
                                    <span class="ei-data-compare-sub">{{ compareBaseFmt(row, "mom") }}</span>
                                </div>
                            </td>
                            <td class="cell-num cell-compare">
                                <div class="ei-data-compare-cell">
                                    <span class="ei-data-compare-main" :class="compareDeltaCls(row, 'yoy')">
                                        {{ compareDeltaFmt(row, "yoy") }}
                                    </span>
                                    <span class="ei-data-compare-sub">{{ compareBaseFmt(row, "yoy") }}</span>
                                </div>
                            </td>
                            <td class="cell-center">
                                <span
                                    v-if="calcRowStatusBadge(row)"
                                    class="ei-data-status ei-data-status--table"
                                    :class="calcRowStatusBadge(row).cls"
                                >
                                    {{ calcRowStatusBadge(row).label }}
                                </span>
                                <span v-else class="ei-data-dash">—</span>
                            </td>
                        </tr>
                    </tbody>
                    <tbody v-else>
                        <tr
                            v-for="row in draftRows"
                            :key="row._rowId"
                            :class="{ 'row-dirty': rowDirty(row), 'row-focus-month': isQueryFocusMonthRow(row) }"
                        >
                            <td
                                class="cell-center cell-editable cell-month-editable"
                                :class="{
                                    'cell-editable--locked': !isEditMode,
                                    'cell-month-editable--active': isEditMode,
                                }"
                            >
                                <DatePicker
                                    v-model="row._monthInput"
                                    type="month"
                                    format="YYYY-MM"
                                    value-type="format"
                                    :clearable="false"
                                    :disabled="!isEditMode || saving"
                                    class="ei-data-month-dp"
                                    :class="{ 'is-locked': !isEditMode || saving }"
                                    @change="onRowMonthChange(row)"
                                />
                            </td>
                            <td class="cell-num">{{ fmt(rowCalc(row)?.refKwhVal) }}</td>
                            <td
                                class="cell-num cell-editable cell-base-editable"
                                :class="{
                                    'cell-editable--locked': !isEditMode,
                                    'cell-base-editable--active': isEditMode,
                                }"
                            >
                                <input
                                    v-model.number="row.inputVal"
                                    class="ei-data-input ei-data-input--cell"
                                    type="number"
                                    step="any"
                                    :disabled="!isEditMode || saving"
                                    @input="markDirty(row)"
                                />
                            </td>
                            <td class="cell-num">{{ fmt(rowCalc(row)?.calcVal) }}</td>
                            <td class="cell-num cell-compare">
                                <div class="ei-data-compare-cell">
                                    <span class="ei-data-compare-main" :class="compareDeltaCls(row, 'mom')">
                                        {{ compareDeltaFmt(row, "mom") }}
                                    </span>
                                    <span class="ei-data-compare-sub">{{ compareBaseFmt(row, "mom") }}</span>
                                </div>
                            </td>
                            <td class="cell-num cell-compare">
                                <div class="ei-data-compare-cell">
                                    <span class="ei-data-compare-main" :class="compareDeltaCls(row, 'yoy')">
                                        {{ compareDeltaFmt(row, "yoy") }}
                                    </span>
                                    <span class="ei-data-compare-sub">{{ compareBaseFmt(row, "yoy") }}</span>
                                </div>
                            </td>
                            <td class="cell-center">
                                <span
                                    v-if="calcRowStatusBadge(row)"
                                    class="ei-data-status ei-data-status--table"
                                    :class="calcRowStatusBadge(row).cls"
                                >
                                    {{ calcRowStatusBadge(row).label }}
                                </span>
                                <span v-else class="ei-data-dash">—</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="ei-data-status-rail">
                <span class="ei-data-status-rail-main" :class="{ 'is-dirty': dirtyCount > 0 }">
                    {{ dirtyCount > 0 ? `저장 대기 ${dirtyCount}건` : "변경 사항 없음" }}
                </span>
                <span v-if="isProductCategory && newRowCount > 0" class="ei-data-status-rail-chip">
                    신규 입력 {{ newRowCount }}건
                </span>
            </div>

            <div v-if="!tableRowsCount" class="ei-data-empty ei-data-empty--in-card">
                {{ emptyGuideText }}
            </div>
        </div>

        <b-modal
            v-model="showFixedInputModal"
            size="md"
            centered
            hide-footer
            title="연도별 기준값 입력"
            body-class="p-0"
        >
            <div class="ei-data-fixed-modal">
                <div class="ei-data-fixed-preview">
                    <div class="ei-data-fixed-preview-item">
                        <span class="ei-data-fixed-preview-k">적용 범위</span>
                        <strong class="ei-data-fixed-preview-v">{{ fixedYearRangeLabel }}</strong>
                    </div>
                    <div class="ei-data-fixed-preview-item">
                        <span class="ei-data-fixed-preview-k">기입력 월</span>
                        <strong class="ei-data-fixed-preview-v">{{ fixedYearRowCount }}개월</strong>
                    </div>
                </div>

                <div class="ei-data-fixed-form">
                    <label class="ei-data-field ei-data-field--year">
                        <span class="ei-data-label">대상 연도</span>
                        <DatePicker
                            v-model="yearInput"
                            type="year"
                            format="YYYY"
                            value-type="format"
                            :clearable="false"
                            class="ei-data-year-dp"
                            @change="onFixedYearChange"
                        />
                        <small class="ei-data-field-help">해당 연도 전체 월(1~12월)에 적용됩니다.</small>
                    </label>
                    <label class="ei-data-field">
                        <span class="ei-data-label">{{ baseColLabel }}</span>
                        <input v-model.number="fixedInputVal" class="ei-data-input" type="number" step="any" />
                        <small class="ei-data-field-help">숫자만 입력하세요.</small>
                    </label>
                </div>
                <div class="ei-data-fixed-actions">
                    <button
                        type="button"
                        class="ei-data-btn ei-data-btn--ghost"
                        :disabled="saving || recalcLoading"
                        @click="closeFixedInputModal"
                    >
                        취소
                    </button>
                    <button
                        type="button"
                        class="ei-data-btn ei-data-btn--primary"
                        :disabled="saving || recalcLoading"
                        @click="saveFixedByYear"
                    >
                        <i v-if="saving" class="fas fa-spinner fa-spin"></i> 저장
                    </button>
                    <button
                        type="button"
                        class="ei-data-btn ei-data-btn--outline-pill"
                        :disabled="saving || recalcLoading"
                        @click="saveFixedByYearAndRecalc"
                    >
                        <i v-if="recalcLoading" class="fas fa-spinner fa-spin"></i> 저장 후 재계산
                    </button>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import backEndApi from "@src/api/backEndApi";
import {
    formatYm,
    ymAddMonths,
    ymFromMonthInput,
    calcStatusBadge,
    mapEnIntCategoryState,
    categoryNameByValue,
    ensureCommonCodes,
    pickLatestCalcPerMonth,
    calcListBoundsFromDatePeriod,
    monthlySeriesMonthFromCalcRow,
    yearFromDatePeriod,
    isValidDatePeriod,
    ENINT_DATA_INPUT_EXISTING,
    ENINT_DATE_TYPE_YEAR,
    ENINT_DATE_TYPE_MONTH,
    EI_DELTA_COLOR_CSS_VARS,
} from "../utils/enIntUi.js";

const PROD_CATEGORY = "PROD";

function nowYearStr() {
    return String(new Date().getFullYear());
}

function nowMonthStr() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default {
    name: "EnIntDataInput",
    components: { DatePicker },
    props: {
        enIntIdx: { type: Number, required: true },
        masterApiState: {
            type: Object,
            default: () => ({ response: null, data: null, loading: false, error: "" }),
        },
        /** EnIntMasterMain에서 단일 getCalcList 결과 */
        sharedCalcListApiState: {
            type: Object,
            default: () => ({ response: null, data: null, loading: false, error: "" }),
        },
        dateType: { type: String, required: true },
        dateValue: { type: Number, required: true },
    },
    data() {
        return {
            eiDeltaColorCssVars: EI_DELTA_COLOR_CSS_VARS,
            saving: false,
            recalcLoading: false,
            syncLoading: false,
            isEditMode: false,
            rows: [],
            draftRows: [],
            snapshotRows: [],
            fixedInputVal: null,
            yearInput: nowYearStr(),
            showFixedInputModal: false,
            calcByYmMap: new Map(),
            monthSeriesByYm: new Map(),
            /** 마지막으로 표에 반영한 조회 키(항목 + calcList YM 구간) — 동일 키로 재조회 중이면 테이블을 비우지 않음 */
            lastStableFetchMeta: null,
        };
    },
    computed: {
        ...mapEnIntCategoryState(),
        master() {
            return this.masterApiState?.data || {};
        },
        isProductCategory() {
            return String(this.master.enIntCategory || "").toUpperCase() === PROD_CATEGORY;
        },
        baseColLabel() {
            const unit = this.master.baseUnit;
            const code = this.master.enIntCategory;
            const label = categoryNameByValue(this.enIntCategoryItems, code);
            const name = label || code || "기준값";
            return unit ? `${name} (${unit})` : name;
        },
        resultColLabel() {
            const unit = this.master.intensityUnit;
            return unit ? `원단위 (${unit})` : "원단위";
        },
        energyUnitLabel() {
            const u = this.master.energyUnit;
            return u ? ` (${u})` : " (kWh)";
        },
        dirtyCount() {
            return this.draftRows.filter((row) => this.rowDirty(row)).length;
        },
        newRowCount() {
            return this.draftRows.filter((row) => row._isNew).length;
        },
        tableRowsCount() {
            return this.draftRows.length;
        },
        emptyGuideText() {
            return this.isProductCategory
                ? "입력된 데이터가 없습니다. 상단 툴바의 추가 아이콘으로 시작해 주세요."
                : "입력된 데이터가 없습니다. 상단 툴바의 입력 아이콘으로 시작해 주세요.";
        },
        fixedYearNumber() {
            const year = parseInt(String(this.yearInput || ""), 10);
            return Number.isFinite(year) ? year : null;
        },
        fixedYearLabel() {
            return this.fixedYearNumber ? `${this.fixedYearNumber}년` : "연도 미선택";
        },
        fixedYearRangeLabel() {
            if (!this.fixedYearNumber) return "—";
            return `${this.fixedYearNumber}-01 ~ ${this.fixedYearNumber}-12`;
        },
        fixedYearRowCount() {
            if (!this.fixedYearNumber) return 0;
            return this.rows.filter((row) => this.getYearByRegYm(row.regYm) === this.fixedYearNumber).length;
        },
        isExistingDataInput() {
            return String(this.master?.dataInputType || "").trim() === ENINT_DATA_INPUT_EXISTING;
        },
        showSummarySyncButton() {
            return this.isExistingDataInput;
        },
        /** 월간일 때 상단 조회 월(YYYYMM), 아니면 null */
        queryFocusYm() {
            if (String(this.dateType || "").toUpperCase() !== ENINT_DATE_TYPE_MONTH) return null;
            const v = Number(this.dateValue);
            if (!Number.isFinite(v)) return null;
            const y = Math.floor(v / 100);
            const m = v % 100;
            if (y < 1990 || y > 2100 || m < 1 || m > 12) return null;
            return v;
        },
        /** enIntIdx·조회 기간 변경 시 상위 calcList와 동기화 */
        dataFetchKey() {
            return `${this.enIntIdx}|${this.dateType}|${this.dateValue}`;
        },
    },
    watch: {
        enIntIdx: {
            immediate: true,
            handler() {
                this.isEditMode = false;
                this.yearInput = nowYearStr();
                this.showFixedInputModal = false;
                this.lastStableFetchMeta = null;
            },
        },
        dataFetchKey: {
            immediate: true,
            handler() {
                this.applyFromSharedCalcList();
            },
        },
        sharedCalcListApiState: {
            deep: true,
            handler() {
                this.applyFromSharedCalcList();
            },
        },
    },
    mounted() {
        ensureCommonCodes(this.$store);
    },
    methods: {
        formatYm,
        calcRowStatusBadge(row) {
            const c = this.rowCalc(row);
            if (!c) return null;
            return calcStatusBadge(c.calcStatus, c.calcMsg);
        },
        fmt(v) {
            if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
            return Number(v).toLocaleString(undefined, { maximumFractionDigits: 6 });
        },
        toMonthInput(ym) {
            const s = String(ym || "");
            if (s.length !== 6) return nowMonthStr();
            return `${s.slice(0, 4)}-${s.slice(4, 6)}`;
        },
        /** API rows 순서 유지 — regYm 최댓값 행만 필요할 때 정렬 대신 선형 탐색 */
        rowWithMaxRegYm(rows) {
            let best = null;
            let maxYm = -Infinity;
            for (const row of Array.isArray(rows) ? rows : []) {
                const ym = Number(row?.regYm);
                if (!Number.isFinite(ym)) continue;
                if (ym >= maxYm) {
                    maxYm = ym;
                    best = row;
                }
            }
            return best;
        },
        toNullableNumber(v) {
            if (v === null || v === undefined || v === "") return null;
            const n = Number(v);
            return Number.isNaN(n) ? null : n;
        },
        getYearByRegYm(ym) {
            const n = Number(ym);
            if (!Number.isFinite(n)) return null;
            return Math.floor(n / 100);
        },
        seedFixedInputFromYear(year) {
            const y = Number(year);
            if (!Number.isFinite(y)) return;
            const match = this.rows.find((row) => this.getYearByRegYm(row.regYm) === y);
            if (match) {
                this.fixedInputVal = this.toNullableNumber(match.inputVal);
                return;
            }
            this.fixedInputVal = null;
        },
        normalizeDraftRows(rows) {
            return (Array.isArray(rows) ? rows : []).map((r, i) => {
                const originRegYm = Number(r.regYm);
                const originInputVal = this.toNullableNumber(r.inputVal);
                return {
                    ...r,
                    regYm: originRegYm,
                    inputVal: originInputVal,
                    _rowId: `${originRegYm}-${i}`,
                    _dirty: false,
                    _isNew: false,
                    _monthInput: this.toMonthInput(originRegYm),
                    _originRegYm: originRegYm,
                    _originInputVal: originInputVal,
                };
            });
        },
        isRowDirty(row) {
            if (!row) return false;
            if (row._isNew) return true;
            const ym = ymFromMonthInput(row._monthInput) || Number(row.regYm);
            const inputVal = this.toNullableNumber(row.inputVal);
            return Number(ym) !== Number(row._originRegYm) || inputVal !== this.toNullableNumber(row._originInputVal);
        },
        /** getCalcList 월별 최신 행의 baseVal을 편집용 inputVal로 쓴다(getDataList 미사용). */
        dataInputRowsFromCalcMap(latestByYm) {
            const list = [];
            for (const r of latestByYm.values()) {
                const regYm = Number(r?.regYm);
                if (!Number.isFinite(regYm)) continue;
                list.push({
                    regYm,
                    inputVal: this.toNullableNumber(r?.baseVal),
                });
            }
            return list;
        },
        /** monthInsight용: 월별 최신 calc → 비교 열에 쓰는 객체 맵 */
        monthSeriesMapFromCalcLatestMap(latestByYm) {
            const map = new Map();
            for (const r of latestByYm.values()) {
                const m = monthlySeriesMonthFromCalcRow(r);
                if (m) map.set(m.regYm, m);
            }
            return map;
        },
        clearCalcTable() {
            this.rows = [];
            this.calcByYmMap = new Map();
            this.monthSeriesByYm = new Map();
            this.snapshotRows = [];
            this.draftRows = [];
            this.lastStableFetchMeta = null;
            if (!this.isProductCategory) {
                this.yearInput = nowYearStr();
                this.fixedInputVal = null;
            }
        },
        /** 상위가 갱신한 sharedCalcListApiState를 기준 연·월 구간만 잘라 표에 반영 */
        applyFromSharedCalcList() {
            try {
                if (!this.enIntIdx || !isValidDatePeriod(this.dateType, this.dateValue)) {
                    this.clearCalcTable();
                    return;
                }
                const bounds = calcListBoundsFromDatePeriod(this.dateType, this.dateValue);
                if (!bounds) {
                    this.clearCalcTable();
                    return;
                }
                const meta = `${this.enIntIdx}|${bounds.fromYm}|${bounds.toYm}`;
                const s = this.sharedCalcListApiState;
                if (!s) {
                    this.clearCalcTable();
                    return;
                }
                /* 동일 조회로 다시 불러오는 동안은 기존 행 유지 → 빈 테이블 깜빡임 방지 */
                if (s.loading) {
                    if (this.lastStableFetchMeta != null && meta === this.lastStableFetchMeta) {
                        return;
                    }
                    this.clearCalcTable();
                    return;
                }
                if (s.error && !s.data) {
                    this.clearCalcTable();
                    return;
                }
                const allRows = Array.isArray(s.data?.rows) ? s.data.rows : [];
                const baseRows = allRows.filter((r) => {
                    const ym = Number(r.regYm);
                    return Number.isFinite(ym) && ym >= bounds.fromYm && ym <= bounds.toYm;
                });
                this.calcByYmMap = pickLatestCalcPerMonth(baseRows);
                this.rows = this.dataInputRowsFromCalcMap(this.calcByYmMap);
                this.monthSeriesByYm = this.monthSeriesMapFromCalcLatestMap(this.calcByYmMap);

                this.snapshotRows = this.rows.map((row) => ({ ...row }));
                this.draftRows = this.normalizeDraftRows(this.snapshotRows);
                this.lastStableFetchMeta = meta;
                if (!this.isProductCategory) {
                    const y = yearFromDatePeriod(this.dateType, this.dateValue);
                    this.yearInput = y != null ? String(y) : nowYearStr();
                    const latest = this.rowWithMaxRegYm(this.rows);
                    this.fixedInputVal = this.toNullableNumber(latest?.inputVal);
                }
            } catch {
                this.clearCalcTable();
            }
        },
        async waitSharedCalcListIdle(timeoutMs = 12000) {
            const t0 = Date.now();
            while (this.sharedCalcListApiState?.loading && Date.now() - t0 < timeoutMs) {
                await new Promise((r) => setTimeout(r, 40));
            }
        },
        async refresh() {
            await this.waitSharedCalcListIdle();
            this.applyFromSharedCalcList();
        },
        rowCalc(row) {
            if (!row || row.regYm == null) return null;
            return this.calcByYmMap.get(Number(row.regYm)) || null;
        },
        /** 월간 조회 시 dateValue(YYYYMM)와 일치하는 행 — regYm 우선, DatePicker 값 보조 */
        rowDisplayYm(row) {
            if (!row) return null;
            const n = Number(row.regYm);
            if (Number.isFinite(n)) return n;
            const mi = row._monthInput;
            if (mi instanceof Date) return mi.getFullYear() * 100 + mi.getMonth() + 1;
            const fromStr = ymFromMonthInput(typeof mi === "string" ? mi : "");
            return fromStr != null ? fromStr : null;
        },
        isQueryFocusMonthRow(row) {
            const focusYm = this.queryFocusYm;
            if (focusYm == null) return false;
            const rowYm = this.rowDisplayYm(row);
            return rowYm != null && rowYm === focusYm;
        },
        monthInsight(row, type) {
            const ym = Number(row?.regYm);
            if (!Number.isFinite(ym)) return null;
            const m = this.monthSeriesByYm.get(ym);
            if (!m) return null;
            if (type === "mom") {
                const rate = m.mom?.rate;
                const delta = m.mom?.delta;
                const baseVal = m.mom?.prevVal;
                return { rate, delta, baseVal };
            }
            const rate = m.yoy?.rate;
            const delta = m.yoy?.delta;
            const baseVal = m.yoy?.yoyVal;
            return { rate, delta, baseVal };
        },
        compareDeltaFmt(row, type) {
            const ins = this.monthInsight(row, type);
            if (!ins || ins.rate == null || !Number.isFinite(Number(ins.rate))) return "—";
            const pct = Number(ins.rate);
            return `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%`;
        },
        compareBaseFmt(row, type) {
            const ins = this.monthInsight(row, type);
            if (!ins || ins.baseVal == null || !Number.isFinite(Number(ins.baseVal))) return "기준 —";
            return `기준 ${this.fmt(ins.baseVal)}`;
        },
        compareDeltaCls(row, type) {
            const ins = this.monthInsight(row, type);
            if (!ins || ins.delta == null || !Number.isFinite(Number(ins.delta))) return "";
            if (Number(ins.delta) > 0) return "ei-data-compare-main--up";
            if (Number(ins.delta) < 0) return "ei-data-compare-main--down";
            return "ei-data-compare-main--flat";
        },
        async toggleEditMode() {
            if (this.saving) return;
            if (!this.isEditMode) {
                this.isEditMode = true;
                return;
            }
            if (this.dirtyCount > 0) {
                const ok = await this.$bvModal.msgBoxConfirm(
                    "편집모드를 종료하면 저장되지 않은 변경 내용은 사라집니다. 계속하시겠습니까?",
                    {
                        title: "편집모드 종료",
                        okTitle: "종료",
                        cancelTitle: "계속 편집",
                        centered: true,
                    },
                );
                if (!ok) return;
                this.resetDraft();
                this.alertNoti("저장되지 않은 변경 내용을 취소했습니다.");
            }
            this.isEditMode = false;
        },
        markDirty(row) {
            row._dirty = this.isRowDirty(row);
        },
        rowDirty(row) {
            return this.isRowDirty(row);
        },
        onRowMonthChange(row) {
            const ym = ymFromMonthInput(row._monthInput);
            if (ym) row.regYm = ym;
            this.markDirty(row);
        },
        openFixedInputModal() {
            if (this.saving) return;
            const latest = this.rowWithMaxRegYm(this.rows);
            const latestYear = this.getYearByRegYm(latest?.regYm);
            this.yearInput = latestYear ? String(latestYear) : nowYearStr();
            this.fixedInputVal = this.toNullableNumber(latest?.inputVal);
            this.showFixedInputModal = true;
        },
        closeFixedInputModal() {
            if (this.saving) return;
            this.showFixedInputModal = false;
        },
        onFixedYearChange(val) {
            const year = parseInt(String(val || ""), 10);
            if (!Number.isFinite(year)) return;
            this.seedFixedInputFromYear(year);
        },
        addNextMonthRow() {
            if (!this.isEditMode) return;
            let nextYm = ymFromMonthInput(nowMonthStr());
            if (this.draftRows.length) {
                const maxYm = Math.max(...this.draftRows.map((r) => Number(r.regYm)).filter((v) => Number.isFinite(v)));
                if (Number.isFinite(maxYm)) nextYm = ymAddMonths(maxYm, 1);
            }
            this.draftRows.push({
                regYm: nextYm,
                inputVal: null,
                _rowId: `new-${Date.now()}-${this.draftRows.length}`,
                _dirty: true,
                _isNew: true,
                _monthInput: this.toMonthInput(nextYm),
                _originRegYm: null,
                _originInputVal: null,
            });
        },
        undoLastAddedRow() {
            for (let i = this.draftRows.length - 1; i >= 0; i -= 1) {
                if (this.draftRows[i]?._isNew) {
                    this.draftRows.splice(i, 1);
                    return;
                }
            }
        },
        resetDraft() {
            this.draftRows = this.normalizeDraftRows(this.snapshotRows);
        },
        async saveFixedByYear(doRecalcAfter = false) {
            const year = parseInt(String(this.yearInput || ""), 10);
            if (!Number.isFinite(year)) return this.alertNoti("연도를 선택하세요.");
            const inputVal = this.toNullableNumber(this.fixedInputVal);
            if (inputVal === null) return this.alertNoti("기준값을 입력하세요.");

            this.saving = true;
            this.recalcLoading = false;
            try {
                const upsertFixedDenominatorByYear_response = await backEndApi.enInt.upsertFixedDenominatorByYear({
                    enIntIdx: this.enIntIdx,
                    year,
                    inputVal: Number(inputVal),
                    rmk: null,
                });
                if (upsertFixedDenominatorByYear_response.status !== 200) throw new Error("api");
                if (doRecalcAfter) {
                    this.saving = false;
                    this.recalcLoading = true;
                    const recalcRange_response = await backEndApi.enInt.recalcRange({
                        enIntIdx: this.enIntIdx,
                        fromYm: year * 100 + 1,
                        toYm: year * 100 + 12,
                    });
                    if (recalcRange_response.status !== 200) throw new Error("recalc");
                    this.alertNoti("저장 후 재계산이 완료되었습니다.");
                } else {
                    this.alertNoti("저장되었습니다. 필요 시 상단에서 재계산을 실행하세요.");
                }
                this.$emit("saved");
                await this.waitSharedCalcListIdle();
                this.showFixedInputModal = false;
            } catch {
                this.alertNoti(doRecalcAfter ? "저장 또는 재계산에 실패했습니다." : "저장에 실패했습니다.");
            } finally {
                this.saving = false;
                this.recalcLoading = false;
            }
        },
        async saveFixedByYearAndRecalc() {
            await this.saveFixedByYear(true);
        },
        async recalcCurrentPeriod() {
            const bounds = calcListBoundsFromDatePeriod(this.dateType, this.dateValue);
            if (!bounds) return this.alertNoti("조회 기간이 올바르지 않습니다.");
            this.recalcLoading = true;
            try {
                const recalcRange_response = await backEndApi.enInt.recalcRange({
                    enIntIdx: this.enIntIdx,
                    fromYm: bounds.fromYm,
                    toYm: bounds.toYm,
                });
                if (recalcRange_response.status !== 200) throw new Error("api");
                this.$emit("saved");
                await this.waitSharedCalcListIdle();
                this.alertNoti("재계산이 완료되었습니다.");
            } catch {
                this.alertNoti("재계산에 실패했습니다.");
            } finally {
                this.recalcLoading = false;
            }
        },
        async syncDenominatorFromSummary() {
            if (!this.isExistingDataInput) return;
            const body = { enIntIdx: this.enIntIdx, recalcAfter: true };
            if (this.dateType === ENINT_DATE_TYPE_YEAR) body.year = this.dateValue;
            else body.regYm = this.dateValue;
            this.syncLoading = true;
            try {
                const syncDenominatorFromSummary_response = await backEndApi.enInt.syncDenominatorFromSummary(body);
                const syncDenominatorFromSummary_data = syncDenominatorFromSummary_response?.data ?? null;
                if (syncDenominatorFromSummary_response.status !== 200) throw new Error("api");
                const n = syncDenominatorFromSummary_data?.count ?? 0;
                const recalcRows = Array.isArray(syncDenominatorFromSummary_data?.recalcRows)
                    ? syncDenominatorFromSummary_data.recalcRows
                    : [];
                let okRecalc = 0;
                for (const r of recalcRows) {
                    if (r && String(r.calcStatus || "").toUpperCase() === "OK") okRecalc += 1;
                }
                this.$emit("saved");
                await this.waitSharedCalcListIdle();
                const recalcPart =
                    recalcRows.length > 0
                        ? ` 재계산 OK ${okRecalc}/${recalcRows.length}건`
                        : "";
                this.alertNoti(`요약 동기화 완료 (${n}건 반영)${recalcPart}`);
            } catch {
                this.alertNoti("동기화에 실패했습니다.");
            } finally {
                this.syncLoading = false;
            }
        },
        async saveRows() {
            const targets = this.draftRows.filter((r) => this.rowDirty(r));
            if (!targets.length) return this.alertNoti("변경된 행이 없습니다.");

            const ymSet = new Set();
            for (const row of this.draftRows) {
                const ym = ymFromMonthInput(row._monthInput);
                if (!ym) return this.alertNoti("유효하지 않은 월이 있습니다.");
                if (ymSet.has(ym)) return this.alertNoti("중복된 월이 있습니다. 월을 확인해 주세요.");
                ymSet.add(ym);
            }

            for (const row of targets) {
                const ym = ymFromMonthInput(row._monthInput);
                if (!ym) return this.alertNoti("유효하지 않은 월이 있습니다.");
                row.regYm = ym;
                const inputVal = this.toNullableNumber(row.inputVal);
                if (inputVal === null) {
                    return this.alertNoti("기준값이 비어 있는 행이 있습니다.");
                }
                row.inputVal = inputVal;
            }

            this.saving = true;
            try {
                const upsertData_jobs = targets.map((row) =>
                    backEndApi.enInt.upsertData({
                        enIntIdx: this.enIntIdx,
                        regYm: Number(row.regYm),
                        inputVal: Number(row.inputVal),
                        rmk: null,
                    }),
                );
                const upsertData_responses = await Promise.all(upsertData_jobs);
                if (upsertData_responses.some((upsertData_response) => upsertData_response?.status !== 200)) {
                    throw new Error("api");
                }
                this.$emit("saved");
                await this.waitSharedCalcListIdle();
                this.alertNoti("저장되었습니다. 필요 시 상단에서 재계산을 실행하세요.");
            } catch {
                this.alertNoti("저장에 실패했습니다.");
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
.ei-data {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    min-height: 0;
}

.ei-data-hint {
    margin: 0;
    padding: 8px 10px;
    border-radius: 6px;
    border: 1px solid rgba(14, 116, 144, 0.12);
    background: rgba(14, 116, 144, 0.05);
    font-size: 12px;
    color: #475569;
    line-height: 1.45;
}

.ei-data-fixed-modal {
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: linear-gradient(180deg, rgba(248, 250, 252, 0.68), rgba(255, 255, 255, 0.98));
}

.ei-data-fixed-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
}

.ei-data-fixed-head-main {
    min-width: 0;
}

.ei-data-fixed-title {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 800;
    color: #0f172a;
}

.ei-data-fixed-title i {
    color: #0e7490;
    font-size: 12px;
}

.ei-data-fixed-desc {
    margin: 4px 0 0;
    font-size: 11px;
    color: #64748b;
    line-height: 1.45;
}

.ei-data-fixed-chip {
    display: inline-flex;
    align-items: center;
    min-height: 22px;
    padding: 2px 8px;
    border-radius: 999px;
    border: 1px solid rgba(14, 116, 144, 0.18);
    background: rgba(14, 116, 144, 0.08);
    color: #0e7490;
    font-size: 10px;
    font-weight: 700;
    white-space: nowrap;
}

.ei-data-fixed-preview {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
}

.ei-data-fixed-preview-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-height: 46px;
    padding: 8px 10px;
    border-radius: 8px;
    border: 1px solid rgba(226, 232, 240, 0.95);
    background: #fff;
}

.ei-data-fixed-preview-k {
    font-size: 10px;
    font-weight: 600;
    color: #64748b;
}

.ei-data-fixed-preview-v {
    font-size: 12px;
    font-weight: 700;
    color: #1e293b;
}

.ei-data-fixed-form {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 9px;
}

.ei-data-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.ei-data-field--year {
    max-width: none;
}

.ei-data-field--full {
    grid-column: 1 / -1;
}

.ei-data-label {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
}

.ei-data-field-help {
    margin-top: 1px;
    font-size: 10px;
    color: #94a3b8;
    line-height: 1.35;
}

.ei-data-fixed-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 4px;
    border-top: 1px solid rgba(226, 232, 240, 0.85);
}

.ei-data-prod-toolbar-wrap {
    display: flex;
    align-items: center;
    justify-content: flex-end;
}

.ei-data-prod-toolbar {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    width: max-content;
    max-width: 100%;
    margin: 0;
    padding: 5px 8px;
    border: 1px solid rgba(226, 232, 240, 0.95);
    border-radius: 999px;
    background: white;
    align-self: flex-start;
}

.ei-data-toolbar-left {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 3px;
    min-width: 0;
    margin-left: 1px;
}

.ei-data-prod-card {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    gap: 0;
    border: 1px solid rgba(226, 232, 240, 0.95);
    border-radius: 10px;
    background: #fff;
    overflow: hidden;
}

.ei-data-action-group {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    min-height: 28px;
}

.ei-data-action-group + .ei-data-action-group {
    border-left: 1px solid rgba(226, 232, 240, 0.95);
    padding-left: 7px;
    margin-left: 3px;
}

.ei-data-toolbar-right {
    display: inline-flex;
    align-items: center;
    margin-right: 1px;
}

.ei-data-input {
    box-sizing: border-box;
    height: 32px;
    padding: 6px 9px;
    border: 1px solid rgba(203, 213, 225, 0.8);
    border-radius: 6px;
    font-size: 12px;
    color: #1b263b;
    background: #fff;
}

.ei-data-field .ei-data-input {
    width: 100%;
}

.ei-data-textarea {
    height: 78px;
    min-height: 78px;
    max-height: 78px;
    padding: 8px 9px;
    line-height: 1.4;
    resize: none;
}

.ei-data-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 6px 10px;
    border-radius: 6px;
    border: 1px solid transparent;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
}

.ei-data-btn--primary {
    background: #0e7490;
    border-color: #0e7490;
    color: #fff;
}

.ei-data-btn--ghost {
    border-color: rgba(203, 213, 225, 0.9);
    background: #fff;
    color: #475569;
}

.ei-data-btn--icon {
    width: 27px;
    height: 27px;
    min-height: 27px;
    padding: 0;
    justify-content: center;
    border: 0;
    border-color: transparent;
    background: transparent;
    color: #475569;
    transition:
        background-color 0.14s ease,
        color 0.14s ease;
}

.ei-data-btn--icon i {
    font-size: 11px;
}

.ei-data-btn--icon:hover:not(:disabled),
.ei-data-btn--icon:focus-visible:not(:disabled) {
    background: rgba(148, 163, 184, 0.12);
    color: #334155;
}

.ei-data-btn--cancel:hover:not(:disabled),
.ei-data-btn--cancel:focus-visible:not(:disabled) {
    background: rgba(185, 28, 28, 0.14);
    color: #b91c1c;
}

.ei-data-btn--save-pill {
    padding: 6px 10px;
    border-radius: 999px;
    background: #fff;
    color: #334155;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.1;
    white-space: nowrap;
}

.ei-data-btn--save-pill:not(:disabled) {
    border-color: #0e7490;
    background: #0e7490;
    color: #fff;
}

.ei-data-btn--save-pill:hover:not(:disabled),
.ei-data-btn--save-pill:focus-visible:not(:disabled) {
    border-color: #0f6b84;
    background: #0f6b84;
    color: #fff;
}

.ei-data-btn--outline-pill {
    padding: 6px 10px;
    border-radius: 999px;
    border: 1px solid rgba(14, 116, 144, 0.45);
    background: #0c6a82;
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.1;
    white-space: nowrap;
}

.ei-data-btn--outline-pill:hover:not(:disabled),
.ei-data-btn--outline-pill:focus-visible:not(:disabled) {
    border-color: #0c6a82;
    background: #0c6a82;
}

.ei-data-edit-toggle {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    justify-content: flex-start;
    padding: 6px 10px;
    border-radius: 999px;
    color: #475569;
    font-size: 11px;
    font-weight: 600;
    transition:
        border-color 0.14s ease,
        background-color 0.14s ease,
        color 0.14s ease;
}

.ei-data-edit-toggle-switch {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 16px;
    padding: 0;
    border: 0;
    border-radius: 999px;
    background: transparent;
    cursor: pointer;
}

.ei-data-edit-toggle-switch:focus-visible {
    outline: 2px solid rgba(14, 116, 144, 0.35);
    outline-offset: 2px;
}

.ei-data-edit-toggle-track {
    width: 30px;
    height: 16px;
    border-radius: 999px;
    background: #cbd5e1;
    position: relative;
    transition: background-color 0.16s ease;
}

.ei-data-edit-toggle-thumb {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #fff;
    transition: transform 0.16s ease;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.2);
}

.ei-data-edit-toggle-label {
    min-width: 0;
    line-height: 1.1;
    white-space: nowrap;
    text-align: left;
    font-variant-numeric: tabular-nums;
}

.ei-data-edit-toggle--on {
    border-color: rgba(14, 116, 144, 0.52);
    background: rgba(14, 116, 144, 0.08);
    color: #0e7490;
}

.ei-data-edit-toggle--on .ei-data-edit-toggle-track {
    background: #0e7490;
}

.ei-data-edit-toggle--on .ei-data-edit-toggle-thumb {
    transform: translateX(14px);
}

.ei-data-edit-toggle--disabled {
    opacity: 0.58;
}

.ei-data-edit-toggle-switch:disabled {
    cursor: default;
}

.ei-data-btn:disabled {
    opacity: 0.4;
    cursor: default;
}

.ei-data-table-wrap {
    width: 100%;
    max-height: 100%;
    overflow: auto;
    flex: 1;
    min-height: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
}

.ei-data-status-rail {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 10px;
    min-height: 28px;
    padding: 5px 8px;
    border-top: 1px solid rgba(226, 232, 240, 0.95);
    border-radius: 0;
    background: rgba(248, 250, 252, 0.72);
}

.ei-data-status-rail-main {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
}

.ei-data-status-rail-main.is-dirty {
    color: #0e7490;
}

.ei-data-status-rail-chip {
    display: inline-flex;
    align-items: center;
    min-height: 18px;
    padding: 0 7px;
    border-radius: 999px;
    border: 1px solid rgba(14, 116, 144, 0.2);
    background: rgba(14, 116, 144, 0.06);
    color: #0e7490;
    font-size: 10px;
    font-weight: 700;
}

.ei-data-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    font-size: 12px;
}

.ei-data-table th {
    padding: 7px 8px;
    border-bottom: 2px solid rgba(226, 232, 240, 0.9);
    background: rgba(248, 250, 252, 0.95);
    font-size: 11px;
    color: #64748b;
    text-align: center;
    position: sticky;
    top: 0;
    z-index: 2;
}

.ei-data-table th.th-month-editable--active,
.ei-data-table th.th-base-editable--active {
    color: #0f766e;
    background: rgba(14, 116, 144, 0.08);
    box-shadow: inset 0 -1px 0 rgba(14, 116, 144, 0.24);
}

.ei-data-table td {
    padding: 6px 8px;
    border-bottom: 1px solid rgba(241, 245, 249, 0.9);
    line-height: 1.35;
}

.ei-data-table tbody tr {
    transition: background-color 0.14s ease;
}

.ei-data-table tbody tr:hover {
    background: rgba(14, 116, 144, 0.03);
}

.row-dirty {
    box-shadow: inset 3px 0 0 rgba(14, 116, 144, 0.45);
}

.row-new {
    background: rgba(59, 130, 246, 0.05);
}

/*
 * 월간 조회: 포커스 월 행 — tr 배경은 td(특히 cell-editable--locked)에 가려지므로 셀에 직접 적용
 */
.ei-data-table tbody tr.row-focus-month > td {
    background-color: rgba(14, 116, 144, 0.07);
}

.ei-data-table tbody tr.row-focus-month:hover > td {
    background-color: rgba(14, 116, 144, 0.1);
}

.ei-data-table tbody tr.row-focus-month > td.cell-editable--locked {
    background-color: rgba(14, 116, 144, 0.075);
}

.ei-data-table tbody tr.row-focus-month:hover > td.cell-editable--locked {
    background-color: rgba(14, 116, 144, 0.105);
}

.ei-data-table tbody tr.row-focus-month.row-dirty > td {
    background-color: rgba(14, 116, 144, 0.085);
}

.ei-data-table tbody tr.row-focus-month.row-dirty:hover > td {
    background-color: rgba(14, 116, 144, 0.11);
}

.ei-data-table tbody tr.row-focus-month > td.cell-month-editable--active,
.ei-data-table tbody tr.row-focus-month > td.cell-base-editable--active {
    background-color: rgba(14, 116, 144, 0.11);
    box-shadow: inset 0 0 0 1px rgba(14, 116, 144, 0.2);
}

.ei-data-table tbody tr.row-focus-month > td.cell-editable:focus-within {
    background-color: rgba(14, 116, 144, 0.14);
}

.cell-center {
    text-align: center;
}

.ei-data-table td.cell-num {
    text-align: right;
    font-variant-numeric: tabular-nums;
}

.cell-compare {
    white-space: nowrap;
}

.ei-data-compare-cell {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 1px;
    line-height: 1.2;
}

.ei-data-compare-main {
    font-size: 11px;
    font-weight: 800;
    color: #64748b;
    font-variant-numeric: tabular-nums;
}

.ei-data-compare-main--up {
    color: var(--ei-delta-up);
}

.ei-data-compare-main--down {
    color: var(--ei-delta-down);
}

.ei-data-compare-main--flat {
    color: #475569;
}

.ei-data-compare-sub {
    font-size: 9px;
    font-weight: 600;
    color: #94a3b8;
    font-variant-numeric: tabular-nums;
}

.cell-base-editable--active {
    background: rgba(14, 116, 144, 0.07);
    box-shadow: inset 0 0 0 1px rgba(14, 116, 144, 0.14);
}

.cell-month-editable--active {
    background: rgba(14, 116, 144, 0.06);
    box-shadow: inset 0 0 0 1px rgba(14, 116, 144, 0.12);
}

.cell-editable--locked {
    background: rgba(248, 250, 252, 0.82);
}

.cell-editable {
    padding: 0 !important;
    position: relative;
    transition: background-color 0.12s ease;
}

.cell-editable:focus-within {
    background: rgba(14, 116, 144, 0.09);
}

.ei-data-input--cell {
    width: 100%;
    min-width: 0;
    padding: 6px 24px 6px 8px;
    border: 0 !important;
    border-radius: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
}

.ei-data-input--cell:focus {
    outline: none;
}

.ei-data-input--cell:disabled {
    color: #64748b;
    cursor: not-allowed;
}

.cell-num .ei-data-input--cell {
    text-align: right;
    font-variant-numeric: tabular-nums;
}

.cell-center .ei-data-input--cell {
    text-align: center;
}

.ei-data-month-dp {
    width: 100%;
    min-width: 0;
}

.ei-data-year-dp {
    width: 100%;
    min-width: 0;
}

.ei-data-month-dp :deep(.mx-datepicker),
.ei-data-month-dp :deep(.mx-input-wrapper),
.ei-data-year-dp :deep(.mx-datepicker),
.ei-data-year-dp :deep(.mx-input-wrapper) {
    display: block;
    width: 100%;
}

.ei-data-month-dp :deep(.mx-input) {
    box-sizing: border-box;
    width: 100%;
    height: 34px;
    padding: 6px 26px 6px 8px;
    border: 0 !important;
    border-radius: 0 !important;
    background: transparent !important;
    color: #1b263b;
    box-shadow: none !important;
}

.ei-data-month-dp.is-locked :deep(.mx-input) {
    color: #64748b;
    cursor: not-allowed;
}

.ei-data-year-dp :deep(.mx-input) {
    box-sizing: border-box;
    width: 100%;
    height: 32px;
    padding: 6px 26px 6px 9px;
    border: 1px solid rgba(203, 213, 225, 0.8);
    border-radius: 6px;
    background: #fff;
    color: #1b263b;
    box-shadow: none;
}

.ei-data-month-dp :deep(.mx-input:focus) {
    outline: none;
}

.ei-data-year-dp :deep(.mx-input:focus) {
    outline: none;
    border-color: rgba(148, 163, 184, 0.9);
}

.cell-center .ei-data-month-dp :deep(.mx-input) {
    text-align: center;
}

.ei-data-month-dp :deep(.mx-icon-calendar),
.ei-data-month-dp :deep(.mx-icon-clear),
.ei-data-year-dp :deep(.mx-icon-calendar),
.ei-data-year-dp :deep(.mx-icon-clear) {
    color: #94a3b8;
}

.ei-data-calc-result {
    padding: 8px 12px;
    border-radius: 6px;
    border: 1px solid rgba(148, 163, 184, 0.3);
    background: #f8fafc;
    font-size: 12px;
}

.ei-data-calc-result.ok {
    border-color: rgba(16, 185, 129, 0.3);
    background: rgba(16, 185, 129, 0.04);
}

.ei-data-calc-result--compact {
    margin-top: 0;
}

.ei-data-calc-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.ei-data-calc-label {
    color: #64748b;
}

.ei-data-status {
    display: inline-block;
    padding: 2px 7px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 700;
}

.ei-data-status.ok {
    background: rgba(16, 185, 129, 0.12);
    color: #047857;
}

.ei-data-status.warn {
    background: rgba(245, 158, 11, 0.12);
    color: #b45309;
}

.ei-data-status.danger {
    background: rgba(239, 68, 68, 0.1);
    color: #b91c1c;
}

.ei-data-status.muted {
    background: rgba(148, 163, 184, 0.12);
    color: #475569;
}

.ei-data-empty {
    font-size: 12px;
    color: #94a3b8;
}

.ei-data-empty--in-card {
    padding: 8px 4px 4px;
}

.ei-data-dash {
    color: #cbd5e1;
    font-size: 11px;
}

.ei-data-status--table {
    padding: 2px 6px;
    font-size: 9px;
}

@media (max-width: 720px) {
    .ei-data-fixed-head {
        flex-direction: column;
        align-items: flex-start;
    }

    .ei-data-fixed-preview,
    .ei-data-fixed-form {
        grid-template-columns: minmax(0, 1fr);
    }

    .ei-data-prod-toolbar {
        max-width: 100%;
        margin: 0;
        padding: 5px 7px;
        flex-wrap: wrap;
        border-radius: 14px;
    }

    .ei-data-toolbar-left {
        flex-wrap: wrap;
        gap: 2px;
    }

    .ei-data-action-group + .ei-data-action-group {
        margin-left: 2px;
        padding-left: 6px;
    }

    .ei-data-btn--icon {
        width: 28px;
        height: 28px;
        min-height: 28px;
    }

    .ei-data-edit-toggle {
        width: auto;
        flex-basis: auto;
    }

    .ei-data-table-wrap {
        max-height: 100%;
    }
}
</style>
